import { Box, Grid, Typography } from "@mui/material";
import { FormikProps } from 'formik';
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateEntityButton } from '../../components/CreateEntityButton';
import EasyStepper from '../../components/EasyStepper';
import Loading from '../../components/loading';
import { PaginationQueryPackage } from '../../models/API/QueryParams/PaginationQueryPackage';
import { CPE } from '../../models/CPE';
import { DNSSuffix } from '../../models/DNSSuffix';
import OrganisationResponse, { CreateOrganisationHostingSiteRequest, CreateOrganisationRequest, OrganisationDNSSuffix, OrganisationHostingSiteSubnet } from "../../models/Organisation";
import { OrganisationContact, OrganisationContactLabel } from "../../models/OrganisationContact";
import { Subnet } from '../../models/Subnet';
import { CreateResponse } from '../../redux/CreateResponse';
import { PermissionEnum } from "../Permission/PermissionEnum";
import { Can } from '../UserApi/logic/Can';
import OrganisationInfoStep from './createOrganisationFlow/BaseInfoStep';
import CPEStep from './createOrganisationFlow/CPEStep';
import DnsSuffixStep, { getDnsSuffixStepValidation } from './createOrganisationFlow/DnsSuffixStep';
import HostingSiteStep, { getHostingSiteStepValidation } from './createOrganisationFlow/HostingSiteStep';
import OrganisationOverviewStep, { getOverviewStepValidation } from './createOrganisationFlow/OverviewStep';
import SubnetStep, { getSubnetStepValidation } from './createOrganisationFlow/SubnetStep';
import { OrganisationInfoDialog } from "./organisationButtons/details/OrganisationInfoDialog";
import { getOrganisationValidationScheme } from './organisationSchemeValidation';
import { useGetDnsSuffixesQuery } from './redux/dnsSuffixApiSlice';
import { useAddOrganisationMutation } from "./redux/organisationApiSlice";
import OrganisationContactsStep, { getOrganisationContactsStepValidation } from "./createOrganisationFlow/OrganisationContactsStep";

export const CreateOrganisationButton = () => {
    const { t } = useTranslation()
    const [open, setOpen] = useState<boolean>(false)
    return (
        <Can I={PermissionEnum.GOD} a={OrganisationResponse} >
            <CreateEntityButton tooltip={t('Create organisation')} open={open} setOpen={setOpen} ignoreOrganisationContext>
                <DrawerContent />
            </CreateEntityButton>
        </Can>
    )
}
export const DrawerContent = () => {
    const [submittedOrganisation, setSubmittedOrganisation] = useState<CreateResponse | undefined>(undefined)
    return (
        <>
            {submittedOrganisation == undefined ?
                <CreateOrganisationButtonContent afterSaveAction={(organisation) => setSubmittedOrganisation(organisation)} /> :
                <OrganisationInfoDialog organisationUuid={submittedOrganisation.uuid} />
            }
        </>
    )
}

export interface CreateOrganisationButtonContentProps {
    afterSaveAction: (organisation: OrganisationResponse) => void
}

export const CreateOrganisationButtonContent = (props: CreateOrganisationButtonContentProps) => {
    const { t } = useTranslation()
    const addOrganisationMutation = useAddOrganisationMutation()
    const [initial, setInitial] = useState<CreateOrganisationRequest>(new CreateOrganisationRequest())
    const allDnsSuffixes = useGetDnsSuffixesQuery({
        pagination: new PaginationQueryPackage()
    });
    const [allowExistingDnsSuffixes, setAllowExistingDnsSuffixes] = useState<boolean>(false)

    useMemo(() => {
        const newOrganisation = new CreateOrganisationRequest();
        newOrganisation.ceos = [new OrganisationContact(OrganisationContactLabel.CEO)];
        newOrganisation.financialManagers = [new OrganisationContact(OrganisationContactLabel.FINANCIAL_MANAGER)];
        newOrganisation.organisationalManagers = [new OrganisationContact(OrganisationContactLabel.ORGANISATIONAL_MANAGER)];
        newOrganisation.securityManagers = [new OrganisationContact(OrganisationContactLabel.SECURITY_MANAGER)];
        newOrganisation.serviceDesks = [new OrganisationContact(OrganisationContactLabel.SERVICE_DESK)];
        newOrganisation.dnsSuffixes = [new OrganisationDNSSuffix()];
        newOrganisation.hosting_sites = [new CreateOrganisationHostingSiteRequest()];
        newOrganisation.subnets = [new OrganisationHostingSiteSubnet()];
        setInitial(newOrganisation);
    }, [])

    if (allDnsSuffixes.isLoading || allDnsSuffixes.data === undefined) {
        return (<Loading />)
    }


    function mapRequestValues(values: CreateOrganisationRequest): CreateOrganisationRequest {
        values.hosting_sites.forEach((hostingSite) => {
            const subnetsForHostingSite = values.subnets
                .filter(subnet => subnet.hosting_site?.uuid === hostingSite.uuid)
                .map(subnet => {
                    const newSubnet = new Subnet();
                    newSubnet.subnet = subnet.subnet;
                    return newSubnet;
                });

            hostingSite.subnets = subnetsForHostingSite;

            const dnsSuffixesForHostingSite = values.dnsSuffixes
                .filter(dnsSuffix => dnsSuffix.hosting_site?.uuid === hostingSite.uuid)
                .map(dnsSuffix => {
                    const newDnsSuffix = new DNSSuffix();
                    newDnsSuffix.dns_suffix = dnsSuffix.dns_suffix;
                    return newDnsSuffix;
                });

            hostingSite.dns_suffixes = dnsSuffixesForHostingSite;

            const cpesForHostingSite = values.cpes
                .filter(cpe => cpe.hosting_site?.uuid === hostingSite.uuid)
                .map(cpe => {
                    const newCPE = new CPE();
                    newCPE.sdn_v4_id = cpe.sdn_v4_id;
                    newCPE.theft_mark = cpe.theft_mark;
                    newCPE.vlan_names = cpe.vlan_names;
                    newCPE.setup_type = cpe.setup_type;
                    newCPE.failover = cpe.failover;
                    return newCPE;
                });

            hostingSite.cpes = cpesForHostingSite;
        })

        values.contacts = values.ceos
            .concat(values.organisationalManagers)
            .concat(values.financialManagers)
            .concat(values.securityManagers)
            .concat(values.serviceDesks)

        return values;
    }

    async function createOrganisation(values: CreateOrganisationRequest) {
        const request = mapRequestValues(values);

        await addOrganisationMutation[0]({
            data: request
        }).unwrap()
            .then(result => props.afterSaveAction(result as OrganisationResponse))
    }

    function NextStepFunc(currentPage: number, _values: CreateOrganisationRequest) {
        return currentPage + 1
    }
    function PreviousStepFunc(currentPage: number) {
        return currentPage - 1
    }

    return (
        <>
            <Box>
                <Grid container justifyContent="center">
                    <Grid item xs={12}>
                        <Typography variant="h6">{t("Create organisation") + ""}</Typography>

                    </Grid>
                </Grid>
                <Grid container justifyContent="center">
                    <Grid item xs={10}>
                        <EasyStepper<CreateOrganisationRequest>
                            nextPage={NextStepFunc}
                            previousPage={PreviousStepFunc}
                            submit={createOrganisation}
                            initialValue={initial}
                            stepValidations={[
                                getOrganisationValidationScheme(),
                                getOrganisationContactsStepValidation(),
                                getHostingSiteStepValidation(),
                                getOrganisationValidationScheme(),
                                getSubnetStepValidation(t),
                                getDnsSuffixStepValidation(allDnsSuffixes.data, t, allowExistingDnsSuffixes),
                                getOverviewStepValidation(),
                            ]}
                            stepTitles={[
                                "Information",
                                "Contacts",
                                "Hosting site",
                                "CPE",
                                "Subnet",
                                "DNS",
                                "Overview",
                            ]}
                            steps={[
                                OrganisationInfoStep,
                                OrganisationContactsStep,
                                HostingSiteStep,
                                CPEStep,
                                SubnetStep,
                                (props: { formikProps: FormikProps<CreateOrganisationRequest>; formPropsStuf: any; }) => DnsSuffixStep(props, allowExistingDnsSuffixes, setAllowExistingDnsSuffixes),
                                OrganisationOverviewStep,
                            ]} />
                    </Grid>
                </Grid>
            </Box>
        </>
    )
}
